import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import type { SxProps, Theme } from "@mui/material/styles";

/**
 * The header bar of a full-screen document preview: Close on the left, the
 * title centred, the document's actions on the right. Lifted out of
 * PdfInlinePreviewDialog (H325, 2026-10-03) so a print-CSS document
 * (PrintDocumentDialog) wears the same chrome; both dialogs render this, so a
 * change to the preview header is a change here.
 */
export const previewHeaderButtonSx: { primary: SxProps<Theme>; secondary: SxProps<Theme> } = {
  secondary: {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.8rem",
    lineHeight: 1,
    minHeight: 32,
    px: 1.25,
    py: 0.4,
    borderRadius: 1,
    whiteSpace: "nowrap",
    color: "#fff",
    backgroundColor: "#9e9e9e",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#616161", boxShadow: "none" },
  },
  primary: {
    backgroundColor: "#008e92",
    color: "#fff",
    "&:hover": { backgroundColor: "#007c80" },
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8rem",
    lineHeight: 1,
    minHeight: 32,
    px: 1.25,
    py: 0.4,
    borderRadius: 1,
    whiteSpace: "nowrap",
    boxShadow: "none",
    "& .MuiButton-startIcon": { mr: 0.5 },
  },
};

interface Props {
  titleId: string;
  title: string;
  onClose: () => void;
  /** The document's buttons (Cancel, Download, Print …), right-aligned. */
  actions: React.ReactNode;
  className?: string;
}

export default function PreviewDialogHeader({ titleId, title, onClose, actions, className }: Props) {
  return (
    <Box
      component="header"
      className={className}
      pl={{ xs: 2, sm: 4 }}
      pr={{ xs: 2, sm: 4 }}
      sx={{
        backgroundColor: grey[200],
        minHeight: "56px",
        alignItems: "center",
        display: "flex",
        flexShrink: 0,
      }}
    >
      <Box sx={{ width: 56, display: "flex", justifyContent: "flex-start" }}>
        <Tooltip title="Close">
          <IconButton onClick={onClose} aria-label="close preview">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, textAlign: "center" }}>
        <Typography id={titleId} variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>

      <Box
        sx={{
          minWidth: 320,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        {actions}
      </Box>
    </Box>
  );
}
